import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Eye, EyeOff, Loader2, LogOut, Trash2 } from "lucide-react";
import { Overline } from "@/components/Reveal";
import Turnstile from "@/components/Turnstile";
import { API, SUBMISSION_LABELS } from "@/data/campaign";
import AdminUpdates from "@/components/AdminUpdates";
import AdminQuestions from "@/components/AdminQuestions";

const ADMIN_TABS = [
  { id: "volunteers", label: "Volunteers" },
  { id: "donations", label: "Donations" },
  { id: "priorities", label: "Priorities" },
  { id: "lawn_signs", label: "Lawn Signs" },
  { id: "updates_subscribers", label: "Updates" },
  { id: "campaign_updates", label: "Campaign Updates" },
  { id: "questions", label: "Questions" },
  { id: "news_items", label: "News Feed" },
  { id: "messages", label: "Messages" },
];

const rowTitle = (row) =>
  [row.first_name, row.last_name].filter(Boolean).join(" ") || row.name || row.email || "Record";

const fmtVal = (k, v) => {
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (k === "amount_cad") return `$${Number(v).toFixed(2)} CAD`;
  return String(v);
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem("ward11_admin_token") || "");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [tsToken, setTsToken] = useState("");
  const tsReset = useRef(null);
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("volunteers");
  const [loading, setLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    if (tab === "campaign_updates" || tab === "questions") {
      setLoading(false);
      return;
    }
    try {
      const { data } =
        tab === "messages"
          ? await axios.get(`${API}/admin/submissions`, { headers })
          : await axios.get(`${API}/admin/table/${tab}`, { headers });
      setItems(data.items);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("ward11_admin_token");
        setToken("");
      } else {
        toast.error("Failed to load records.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, tab]);

  useEffect(() => {
    load();
  }, [load]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email: loginForm.email.trim(), password: loginForm.password.trim(), turnstile_token: tsToken },
        { withCredentials: true },
      );
      localStorage.setItem("ward11_admin_token", data.access_token);
      setToken(data.access_token);
      toast.success(`Welcome back, ${data.name}.`);
    } catch (e) {
      const d = e.response?.data?.detail;
      setLoginError(typeof d === "string" ? d : "Login failed. Check your credentials.");
      tsReset.current?.();
      setTsToken("");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    localStorage.removeItem("ward11_admin_token");
    setToken("");
  };

  const markRead = async (id) => {
    await axios.patch(`${API}/admin/submissions/${id}/read`, {}, { headers });
    setItems((list) => list.map((s) => (s.id === id ? { ...s, read: true } : s)));
    load();
  };

  const remove = async (id) => {
    await axios.delete(`${API}/admin/submissions/${id}`, { headers });
    setItems((list) => list.filter((s) => s.id !== id));
    toast.success("Submission deleted.");
  };

  const setSignStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/lawn-signs/${id}/status`, { status }, { headers });
      toast.success(`Marked as ${status}.`);
      load();
    } catch {
      toast.error("Could not update status.");
    }
  };

  const [showSecurity, setShowSecurity] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "" });
  const [pwSaving, setPwSaving] = useState(false);

  const changePw = async () => {
    setPwSaving(true);
    try {
      await axios.post(`${API}/admin/change-password`, pwForm, { headers });
      toast.success("Admin password updated.");
      setPwForm({ current_password: "", new_password: "" });
      setShowSecurity(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update password.");
    } finally {
      setPwSaving(false);
    }
  };

  if (!token) {
    return (
      <main data-testid="admin-login-page" className="flex min-h-screen items-center justify-center bg-pine-950 px-5 pt-20">
        <form onSubmit={login} className="w-full max-w-md border border-paper/15 bg-pine-900 p-6 text-paper sm:p-10">
          <Overline light>Campaign Hub</Overline>
          <h1 className="mt-3 font-serif text-3xl font-medium tracking-tight">Admin Sign In</h1>
          <div className="mt-8 space-y-5">
            <label className="block">
              <span className="mb-1.5 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-paper/60">Email</span>
              <input
                type="email"
                required
                data-testid="admin-email-input"
                value={loginForm.email}
                onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))}
                className="w-full border border-paper/20 bg-pine-950 px-4 py-3 text-sm text-paper placeholder:text-paper/30 focus:border-terra-400 focus:outline-none"
                placeholder="admin email"
              />
            </label>
            <label className="block">
              <span className="mb-1.5 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-paper/60">Password</span>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  required
                  data-testid="admin-password-input"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))}
                  className="w-full border border-paper/20 bg-pine-950 px-4 py-3 pr-12 text-sm text-paper placeholder:text-paper/30 focus:border-terra-400 focus:outline-none"
                  placeholder="password"
                />
                <button
                  type="button"
                  data-testid="admin-password-toggle"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-paper/50 transition-colors hover:text-paper"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </label>
          </div>
          <div className="mt-6">
            <Turnstile action="admin-login" onToken={setTsToken} resetRef={tsReset} />
          </div>
          {loginError && (
            <p data-testid="admin-login-error" className="mt-4 border border-red-400/40 bg-red-400/10 px-4 py-3 text-sm text-red-300">
              {loginError}
            </p>
          )}
          <button
            type="submit"
            disabled={loggingIn || !tsToken}
            data-testid="admin-login-button"
            className="mt-8 flex w-full items-center justify-center gap-2 bg-terra-500 px-7 py-4 text-sm font-semibold text-paper transition-colors hover:bg-terra-600 disabled:opacity-60"
          >
            {loggingIn && <Loader2 className="h-4 w-4 animate-spin" />}
            Sign In
          </button>
        </form>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="mx-auto max-w-7xl px-5 pb-24 pt-28 sm:px-8 lg:pt-32">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Overline>Campaign Hub</Overline>
          <h1 className="mt-3 font-serif text-4xl font-medium tracking-tight">Submissions</h1>
        </div>
        <button
          onClick={() => setShowSecurity((s) => !s)}
          data-testid="admin-security-toggle"
          className="flex items-center gap-2 border border-pine-700/25 px-5 py-2.5 text-sm font-semibold text-pine-700 transition-colors hover:border-terra-500 hover:text-terra-500"
        >
          Security
        </button>
        <button
          onClick={logout}
          data-testid="admin-logout-button"
          className="flex items-center gap-2 border border-pine-700/25 px-5 py-2.5 text-sm font-semibold text-pine-700 transition-colors hover:border-terra-500 hover:text-terra-500"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>

      {showSecurity && (
        <div data-testid="admin-security-panel" className="mt-6 max-w-lg border border-pine-700/15 bg-white/60 p-6">
          <h2 className="font-serif text-xl font-medium tracking-tight">Change admin password</h2>
          <div className="mt-4 space-y-4">
            <label className="block">
              <span className="mb-1.5 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-ink/60">Current Password</span>
              <input type="password" data-testid="pw-current-input" value={pwForm.current_password} onChange={(e) => setPwForm((f) => ({ ...f, current_password: e.target.value }))} className="w-full border border-pine-700/25 bg-paper px-4 py-3 text-sm focus:border-terra-500 focus:outline-none" />
            </label>
            <label className="block">
              <span className="mb-1.5 block font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-ink/60">New Password (min 12 characters)</span>
              <input type="password" data-testid="pw-new-input" value={pwForm.new_password} onChange={(e) => setPwForm((f) => ({ ...f, new_password: e.target.value }))} className="w-full border border-pine-700/25 bg-paper px-4 py-3 text-sm focus:border-terra-500 focus:outline-none" />
            </label>
            <button onClick={changePw} disabled={pwSaving} data-testid="pw-save-button" className="flex items-center gap-2 bg-pine-700 px-6 py-3 text-sm font-semibold text-paper transition-colors hover:bg-terra-500 disabled:opacity-60">
              {pwSaving && <Loader2 className="h-4 w-4 animate-spin" />}
              Update Password
            </button>
          </div>
        </div>
      )}

      <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-tabs">
        {ADMIN_TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`border px-4 py-2.5 text-xs font-semibold transition-colors ${
              tab === t.id
                ? "border-pine-700 bg-pine-700 text-paper"
                : "border-pine-700/25 text-ink/70 hover:border-terra-500 hover:text-terra-500"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "campaign_updates" ? (
        <div className="mt-6">
          <AdminUpdates headers={headers} />
        </div>
      ) : tab === "questions" ? (
        <div className="mt-6">
          <AdminQuestions headers={headers} />
        </div>
      ) : (
        <div className="mt-6 space-y-4" data-testid="admin-records-list">
        {loading ? (
          <div className="flex items-center gap-2 py-16 text-ink/50">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <p className="border border-dashed border-pine-700/25 py-16 text-center text-sm text-ink/50" data-testid="admin-empty-state">
            No records yet in {ADMIN_TABS.find((t) => t.id === tab)?.label}.
          </p>
        ) : tab === "messages" ? (
          items.map((s) => (
            <div
              key={s.id}
              data-testid={`submission-card-${s.id}`}
              className={`border p-6 transition-colors ${s.read ? "border-pine-700/15 bg-white/50" : "border-terra-500/50 bg-terra-100/40"}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="bg-pine-700 px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-paper">
                      {SUBMISSION_LABELS[s.type] || s.type}
                    </span>
                    {!s.read && <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-terra-500">New</span>}
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-ink/45">
                      {new Date(s.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="mt-3 font-serif text-xl font-semibold tracking-tight">{s.name}</p>
                  <p className="mt-1 text-sm text-ink/65">
                    {s.email}
                    {s.phone ? ` · ${s.phone}` : ""}
                    {s.neighbourhood ? ` · ${s.neighbourhood}` : ""}
                  </p>
                  {s.message && <p className="mt-3 max-w-2xl border-l-2 border-terra-500 pl-4 text-sm italic leading-relaxed text-ink/75">{s.message}</p>}
                </div>
                <div className="flex gap-2">
                  {!s.read && (
                    <button
                      onClick={() => markRead(s.id)}
                      data-testid={`submission-read-${s.id}`}
                      className="flex h-9 w-9 items-center justify-center border border-pine-700/25 text-pine-700 transition-colors hover:border-pine-700 hover:bg-pine-700 hover:text-paper"
                      aria-label="Mark as read"
                    >
                      <Check className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={() => remove(s.id)}
                    data-testid={`submission-delete-${s.id}`}
                    className="flex h-9 w-9 items-center justify-center border border-pine-700/25 text-pine-700 transition-colors hover:border-red-600 hover:bg-red-600 hover:text-white"
                    aria-label="Delete submission"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          items.map((row) => (
            <div key={row.id} data-testid={`row-${row.id}`} className="border border-pine-700/15 bg-white/60 p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="font-serif text-lg font-semibold tracking-tight">{rowTitle(row)}</p>
                    {row.payment_status && (
                      <span className={`px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] ${row.payment_status === "succeeded" ? "bg-pine-700 text-paper" : "bg-parch text-ink/60"}`}>
                        {row.payment_status}
                      </span>
                    )}
                    {row.status && (
                      <span className="bg-parch px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-ink/60">
                        {row.status}
                      </span>
                    )}
                  </div>
                  <div className="mt-3 grid gap-x-8 gap-y-2 sm:grid-cols-2">
                    {Object.entries(row)
                      .filter(([k, v]) => !["id", "created_at", "first_name", "last_name", "name", "payment_status", "status"].includes(k) && v !== "" && v !== null)
                      .map(([k, v]) => (
                        <p key={k} className="text-sm text-ink/70">
                          <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-ink/45">{k.replace(/_/g, " ")}</span>
                          <br />
                          {fmtVal(k, v)}
                        </p>
                      ))}
                  </div>
                  {tab === "lawn_signs" && (
                    <div className="mt-4 flex flex-wrap gap-2" data-testid={`sign-actions-${row.id}`}>
                      {["requested", "scheduled", "installed"].map((s) => (
                        <button
                          key={s}
                          data-testid={`sign-${s}-${row.id}`}
                          onClick={() => setSignStatus(row.id, s)}
                          className={`border px-3.5 py-2 text-xs font-semibold capitalize transition-colors ${
                            row.status === s
                              ? "border-pine-700 bg-pine-700 text-paper"
                              : "border-pine-700/25 text-ink/60 hover:border-terra-500 hover:text-terra-500"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-ink/45">
                  {new Date(row.created_at).toLocaleString()}
                </span>
              </div>
            </div>
          ))
        )}
        </div>
      )}
    </main>
  );
};

export default Admin;
